import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Card,
  Spin,
  Typography,
  Descriptions,
  Tag,
  Button,
  message,
  Modal,
  Input,
  Form,
  Alert,
  Menu,
  Space,
  Avatar,
  Dropdown,
} from "antd";
import { axiosInstance } from "src/shared/api/axiosInstance";
import moment from "moment";
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { ImageList } from "src/features/Lists";

const { Title, Text } = Typography;


const OrderCourier = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isAuthModalVisible, setIsAuthModalVisible] = useState(false);
  const [isUserDataModalVisible, setIsUserDataModalVisible] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("authToken") || "");
  const [authError, setAuthError] = useState(null);
  const [form] = Form.useForm();
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  const fetchOrderData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/docs_sales/verify/${hash}?order_id=${id}&role=courier`
      );
      setOrderData(response.data);
  
      if (
        response.data?.delivery?.recipient &&
        typeof response.data.delivery.recipient === "string"
      ) {
        try {
          response.data.delivery.recipient = JSON.parse(
            response.data.delivery.recipient
          );
        } catch (e) {
          console.error("Ошибка парсинга данных получателя:", e);
        }
      }
  
      setError(null);
    } catch (err) {
      console.error("Ошибка при загрузке данных заказа:", err);
      setError(
        "Не удалось загрузить данные заказа. Проверьте корректность ссылки."
      );
    } finally {
      setLoading(false);
    }
  }, [id, hash]);

  const fetchUserData = useCallback(async (userToken) => {
    try {
      setUserLoading(true);
      const response = await axiosInstance.get(`/users/?token=${userToken}`);
      setUserData(response.data);
      return response.data;
    } catch (err) {
      console.error("Ошибка при получении данных пользователя:", err);
      message.error("Не удалось получить информацию о пользователе");
      return null;
    } finally {
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id && hash) {
      fetchOrderData();
    }

    const savedToken = localStorage.getItem("authToken");
    if (savedToken) {
      fetchUserData(savedToken);
    }
  }, [id, hash, fetchOrderData, fetchUserData]);

  const handleAuthSubmit = () => {
    if (!token.trim()) {
      setAuthError("Токен не может быть пустым");
      return;
    }

    localStorage.setItem("authToken", token);
    setAuthError(null);
    setIsAuthModalVisible(false);
    message.success("Токен сохранен успешно");

    fetchUserData(token);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    message.success("Вы успешно вышли из системы");
    window.location.reload();
  };

  const handleAssignCourier = async () => {
    try {
      const savedToken = localStorage.getItem("authToken");
      if (!savedToken) {
        message.error("Необходим токен для действия");
        setIsAuthModalVisible(true);
        return;
      }

      setActionLoading(true);

      let user = userData;
      if (!user) {
        user = await fetchUserData(savedToken);
        if (!user) {
          message.error("Не удалось получить данные пользователя");
          setActionLoading(false);
          return;
        }
      }

      if (orderData.status !== "collected") {
        message.warning(
          "Заказ должен быть полностью собран перед назначением курьера"
        );
        setActionLoading(false);
        return;
      }

      await axiosInstance.patch(
        `/docs_sales/${id}/assign/courier?token=${savedToken}`,
        {
          user_id: user.id,
        }
      );

      message.success("Вы успешно назначены доставщиком этого заказа");
      fetchOrderData();
    } catch (err) {
      console.error("Ошибка при назначении доставщика:", err);
      if (err.response && err.response.status === 401) {
        message.error(
          "Пользователь не авторизован или отсутствует в учетной записи"
        );
      } else {
        message.error("Не удалось назначить доставщика");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePickOrder = () => {
    if (orderData.status !== "collected") {
      message.warning(
        "Заказ должен быть полностью собран перед тем, как его можно забрать"
      );
      return;
    }

    if (userData) {
      form.setFieldsValue({
        name: userData.first_name || "",
        phone: userData.phone_number || "",
      });
    }

    setIsUserDataModalVisible(true);
  };

  const handleUserDataSubmit = async (values) => {
    try {
      const savedToken = localStorage.getItem("authToken");
      if (!savedToken) {
        message.error("Необходим токен для действия");
        setIsAuthModalVisible(true);
        return;
      }

      setActionLoading(true);

      let userId = userData?.id || 0;

      if (values.name || values.phone) {
        await axiosInstance.patch(
          `/docs_sales/${id}/assign/courier?token=${savedToken}`,
          {
            user_id: userId,
            name: values.name,
            phone: values.phone,
          }
        );
      }

      await axiosInstance.patch(`/docs_sales/${id}/status?token=${savedToken}`, {
        status: "picked",
        comment: "Заказ забран курьером",
      });

      setIsUserDataModalVisible(false);
      message.success("Заказ успешно забран");
      fetchOrderData();
    } catch (err) {
      console.error("Ошибка при обновлении статуса:", err);
      message.error("Не удалось обновить статус заказа");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeliverOrder = async () => {
    try {
      const savedToken = localStorage.getItem("authToken");
      if (!savedToken) {
        message.error("Необходим токен для действия");
        setIsAuthModalVisible(true);
        return;
      }

      setActionLoading(true);
      await axiosInstance.patch(`/docs_sales/${id}/status?token=${savedToken}`, {
        status: "delivered",
        comment: "Заказ доставлен получателю",
      });

      message.success("Заказ успешно доставлен");
      fetchOrderData();
    } catch (err) {
      console.error("Ошибка при обновлении статуса:", err);
      message.error("Не удалось обновить статус заказа");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDelivery = async () => {
    try {
      const savedToken = localStorage.getItem("authToken");
      if (!savedToken) {
        message.error("Необходим токен для действия");
        setIsAuthModalVisible(true);
        return;
      }

      setActionLoading(true);
      await axiosInstance.patch(`/docs_sales/${id}/status?token=${savedToken}`, {
        status: "cancelled",
        comment: "Доставка отменена: отказ клиента или нет возможности",
      });

      message.success("Доставка отменена");
      fetchOrderData();
    } catch (err) {
      console.error("Ошибка при отмене доставки:", err);
      message.error("Не удалось отменить доставку");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      received: { color: "blue", text: "Получен" },
      processed: { color: "purple", text: "Обработан" },
      collecting: { color: "orange", text: "Собирается" },
      collected: { color: "cyan", text: "Собран" },
      picked: { color: "geekblue", text: "Забран" },
      delivered: { color: "green", text: "Доставлен" },
      cancelled: { color: "red", text: "Отменён" },
    };

    const defaultStatus = { color: "default", text: status };
    const currentStatus = statusMap[status] || defaultStatus;

    return <Tag color={currentStatus.color}>{currentStatus.text}</Tag>;
  };

  const avatarMenu = (
    <Menu style={{ width: "fit-content" }}>
      <Menu.Item disabled>
        <Space
          style={{ width: "100%", justifyContent: "center", padding: "5px" }}
        >
          <Avatar
            size={60}
            // eslint-disable-next-line no-undef
            src={`https://${process.env.REACT_APP_APP_URL}/${userData?.photo}`}
          />
        </Space>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="user-info" disabled>
        <Space style={{ columnGap: 5 }}>
          <Text style={{ fontSize: 14 }}>Здравствуйте,</Text>
          <Text strong style={{ fontSize: 14 }}>
            {userData?.first_name || userData?.username || "Пользователь"}
          </Text>
        </Space>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item style={{ justifyContent: "center" }}>
        <Button
          style={{ width: "100%" }}
          icon={<LogoutOutlined />}
          onClick={() => handleLogout()}
        >
          Выйти
        </Button>
      </Menu.Item>
    </Menu>
  );

  const renderActionButtons = () => {
    if (!orderData.assigned_courier) {
      if (orderData.status === "collected") {
        return (
          <Button
            type="primary"
            onClick={handleAssignCourier}
            loading={actionLoading || userLoading}
          >
            Я доставлю
          </Button>
        );
      } else if (
        orderData.status === "received" ||
        orderData.status === "processed" ||
        orderData.status === "collecting"
      ) {
        return (
          <Alert
            message="Заказ еще собирается"
            description="Дождитесь завершения сборки заказа, прежде чем браться за доставку"
            type="info"
            showIcon
          />
        );
      } else {
        return (
          <Alert
            message="Недоступно"
            description="Заказ находится в статусе, который не позволяет назначить курьера"
            type="warning"
            showIcon
          />
        );
      }
    }

    if (orderData.status === "collected") {
      return (
        <>
          <Button
            type="primary"
            onClick={handlePickOrder}
            loading={actionLoading}
            style={{ marginRight: "10px" }}
          >
            Я забрал(а)
          </Button>
          <Button danger onClick={handleCancelDelivery} loading={actionLoading}>
            Отмена (отказ клиента/нет возможности)
          </Button>
        </>
      );
    } else if (orderData.status === "picked") {
      return (
        <>
          <Button
            type="primary"
            onClick={handleDeliverOrder}
            loading={actionLoading}
            style={{ marginRight: "10px" }}
          >
            Я доставил(а)
          </Button>
          <Button danger onClick={handleCancelDelivery} loading={actionLoading}>
            Отмена (отказ клиента/нет возможности)
          </Button>
        </>
      );
    } else if (orderData.status === "delivered") {
      return (
        <Alert
          message="Доставка завершена"
          description="Заказ успешно доставлен получателю"
          type="success"
          showIcon
        />
      );
    } else if (orderData.status === "cancelled") {
      return (
        <Alert
          message="Доставка отменена"
          description="Заказ был отменен"
          type="error"
          showIcon
        />
      );
    } else {
      return (
        <Alert
          message="Ожидание"
          description="Дождитесь завершения предыдущих этапов обработки заказа"
          type="info"
          showIcon
        />
      );
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px" }}>
        <Alert message="Ошибка" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <Card
        title={<Title level={3}>Заказ #{orderData?.number} (Доставка)</Title>}
      >
        <Descriptions bordered>
          <Descriptions.Item label="Статус" span={3}>
            {orderData?.status && getStatusTag(orderData.status)}
          </Descriptions.Item>
          <Descriptions.Item label="ID" span={3}>
            {orderData?.id}
          </Descriptions.Item>
          <Descriptions.Item label="Стоимость доставки" span={3}>
  {(orderData?.delivery_info?.delivery_price ?? orderData?.delivery?.delivery_price ?? orderData?.delivery_price) != null
    ? `${orderData?.delivery_info?.delivery_price ?? orderData?.delivery?.delivery_price ?? orderData?.delivery_price} руб.`
    : "Не указана"}
</Descriptions.Item>
        </Descriptions>
        

        <Card title="Информация о доставке" style={{ marginTop: "20px" }}>
        {orderData?.delivery ? (
            <>
              <p>
                <strong>Адрес доставки:</strong>{" "}
                {orderData.delivery.address || "Не указан"}
              </p>
              <p>
                <strong>Дата доставки:</strong>{" "}
                {orderData.delivery.delivery_date
                  ? moment(orderData.delivery.delivery_date).format(
                      "DD.MM.YYYY HH:mm"
                    )
                  : "Не указана"}
              </p>

              <Card
                size="small"
                title="Получатель"
                style={{ marginTop: "10px" }}
              >
                {orderData.delivery.recipient ? (
                  <>
                    <p>
                      <strong>Имя:</strong>{" "}
                      {orderData.delivery.recipient.name || "Не указано"}
                    </p>
                    <p>
                      <strong>Фамилия:</strong>{" "}
                      {orderData.delivery.recipient.surname || "Не указана"}
                    </p>
                    <p>
                      <strong>Телефон:</strong>{" "}
                      {orderData.delivery.recipient.phone || "Не указан"}
                    </p>
                  </>
                ) : (
                  <p>Информация о получателе отсутствует</p>
                )}
              </Card>

              {orderData.delivery.note && (
                <p style={{ marginTop: "10px" }}>
                  <strong>Примечание:</strong> {orderData.delivery.note}
                </p>
              )}
            </>
          ) : (
            <Alert message="Информация о доставке отсутствует" type="warning" />
          )}
        </Card>

        {orderData.status !== "received" &&
          orderData.status !== "processed" &&
          orderData.status !== "collecting" &&
          orderData.assigned_courier && (
            <ImageList
              token={token}
              entity="docs_sales_delivery_info"
              entity_id={orderData?.id}
            />
          )}

        {userData && (
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <Alert
              message="Информация о пользователе"
              description={`Вы авторизованы как: ${userData.first_name || ""} ${
                userData.last_name || ""
              } (ID: ${userData.id})`}
              type="info"
              showIcon
            />
          </div>
        )}

        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {renderActionButtons()}
        </div>
      </Card>

      <Modal
        title="Авторизация"
        open={isAuthModalVisible}
        onOk={handleAuthSubmit}
        onCancel={() => setIsAuthModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <p>Введите ваш токен для доступа к API:</p>
        <Input.Password
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Введите токен"
        />
        {authError && (
          <div style={{ color: "red", marginTop: "5px" }}>{authError}</div>
        )}
        <div style={{ marginTop: "10px" }}>
          <a href="https://t.me/tablecrmbot" target="_blank" rel="noreferrer">
            У вас нет токена? Получите его в Telegram боте @tablecrmbot при
            прглашении в кассу
          </a>
        </div>
      </Modal>

      <Modal
        title="Введите данные доставщика"
        open={isUserDataModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsUserDataModalVisible(false)}
        okText="Подтвердить"
        cancelText="Отмена"
        confirmLoading={actionLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleUserDataSubmit}>
          <Form.Item label="Ваше имя" name="name">
            <Input placeholder="Введите ваше имя" />
          </Form.Item>
          <Form.Item label="Ваш телефон" name="phone">
            <Input placeholder="Введите ваш телефон" />
          </Form.Item>
          <div style={{ marginTop: "10px" }}>
            <Alert
              message="Информация"
              description="Эти данные помогут получателю идентифицировать вас как доставщика. Вы также можете авторизоваться через Telegram, чтобы эти данные заполнились автоматически."
              type="info"
              showIcon
            />
          </div>
        </Form>
      </Modal>

      <div style={{ position: "fixed", top: "10px", right: "10px" }}>
        {userData ? (
          <Dropdown overlay={avatarMenu} trigger={["click"]}>
            <Avatar
            // eslint-disable-next-line no-undef
              src={`https://${process.env.REACT_APP_APP_URL}/${userData?.photo}`}
              style={{ marginRight: 10, cursor: "pointer" }}
              size={50}
              icon={!userData?.photo && <UserOutlined />}
            />
          </Dropdown>
        ) : (
          <Button type="primary" onClick={() => setIsAuthModalVisible(true)}>
            Авторизоваться
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderCourier;
